import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, IconButton, useTheme } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusy from '@mui/icons-material/EventBusy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { csn, logRender } from '@one-vision/utils';
import { DzSearchInput } from '../../shared/dz-search-input';
import { isAfter, isFuture, isPast, isToday, } from '@one-vision/date-utils';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { DzProjectStage, } from '../../../types';
import { DzLeadIcon, DzLostIcon, DzServiceClientIcon, DzActiveProjectIcon, } from '../../icons';
import { DzEmpty } from '../../shared/dz-empty';
import { useStyles as useOrgStyles } from '../dz-address-panel.styles';
import { DzCalendarPicker } from './dz-calendar-picker';
import { DzMenuView } from './dz-menu.view';
import { DzProjectsDataRow } from './dz-projects-data-row';
import { useStyles } from './dz-projects.styles';
import DzOwnerAutocomplete from './dz-owner-autocomplete';
import { useModalManager } from '../../shared/dz-modal-manager';
import { ModalIDs, WizardStep } from '../../../const';
import { DzCardHeaderName } from '../../shared/dz-card-header-name';
import { Projects } from '../../../redux';
export const DzProjectsView = ({ projects, primaryClient, ovaid, address, owners, thunks, actions, dispatch, zendesk, apiClient, products, brands, partner, labels, allClients, onCardNameClick = () => undefined, }) => {
    logRender(DzProjectsView);
    const classes = useStyles();
    const theme = useTheme();
    const orgClasses = useOrgStyles();
    const [clientsLoading, setClientsLoading] = useState(false);
    useEffect(() => {
        setClientsLoading(true);
        dispatch(thunks.fetchClientsForAddress({
            ovaid: ovaid,
        }))
            .unwrap()
            .then(() => {
            setClientsLoading(false);
        });
    }, [ovaid, dispatch]);
    const [projectMenuInfo, setProjectMenuInfo] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const { openModal } = useModalManager();
    const projectsList = useMemo(() => projects
        ? projects.filter((project) => {
            if (!searchText) {
                return true;
            }
            const searchTextLowerCase = searchText.toLowerCase();
            return !!Object.values(project).find((value) => String(value)
                .toString()
                .toLowerCase()
                .includes(searchTextLowerCase));
        })
        : projects, [projects, searchText]);
    const handleDateChange = useCallback((date, projectId, fieldKey) => {
        if (date) {
            dispatch(thunks.updateProject({
                changes: {
                    [fieldKey]: date,
                },
                projectId,
            }))
                .unwrap()
                .then(() => {
                if (ovaid) {
                    dispatch(thunks.fetchOrgDialogProjects({
                        ovaid,
                    }));
                }
                dispatch(Projects.actions.update({
                    ovprjid: projectId,
                    [fieldKey]: date,
                }));
            });
        }
    }, [dispatch, thunks, address, projectsList]);
    const searchHandler = useCallback((text) => {
        setSearchText(text);
    }, [setSearchText]);
    const openNewProjectDialog = useCallback(async () => {
        const result = await openModal(ModalIDs.createProject, {
            apiClient,
            actions,
            dispatch,
            thunks,
            labels,
            allClients,
            owners,
            products,
            preselectedAddress: address || undefined,
            preselectedClient: primaryClient,
            preselectedStep: primaryClient
                ? WizardStep.ProjectDetails
                : WizardStep.AddPrimaryContact,
            zendesk: zendesk,
            partner,
        });
        if (!result) {
            return;
        }
        dispatch(thunks.fetchOrgDialogProjects({
            ovaid,
        }));
        dispatch(actions.addressIsUpdating({ tosAccepted: result.tosAccepted }));
    }, [address, dispatch, owners, products, brands, primaryClient]);
    const projectsContent = useMemo(() => {
        return projectsList === null || projectsList === void 0 ? void 0 : projectsList.map((project) => {
            const getIsFuture = () => {
                return (!isToday(new Date(project.day1StartDate)) &&
                    isFuture(new Date(project.day1StartDate)));
            };
            const getProjectStageIcon = (stageId) => {
                switch (stageId) {
                    case DzProjectStage.ServiceClient:
                        if (getIsFuture()) {
                            return _jsx(DzActiveProjectIcon, { fontSize: "small" });
                        }
                        return _jsx(DzServiceClientIcon, { fontSize: "small" });
                    case DzProjectStage.ActiveProject:
                        return _jsx(DzActiveProjectIcon, { fontSize: "small" });
                    case DzProjectStage.Lead:
                        return _jsx(DzLeadIcon, { fontSize: "small" });
                    case DzProjectStage.LostProposal:
                        return _jsx(DzLostIcon, { fontSize: "small" });
                    default:
                        return null;
                }
            };
            const shouldShowLost = (stageId) => stageId === DzProjectStage.ActiveProject ||
                stageId === DzProjectStage.Lead;
            const shouldShowSubmitDay1 = (stageId) => stageId === DzProjectStage.ActiveProject;
            const shouldShowWonProposal = (stageId) => stageId === DzProjectStage.Lead;
            const handleMenuClick = (event) => {
                if (!address) {
                    return;
                }
                setProjectMenuInfo({
                    anchor: event.target,
                    projectId: project.ovprjid,
                    showLost: shouldShowLost(project.projectStageId),
                    showSubmitDay1: shouldShowSubmitDay1(project.projectStageId),
                    estimatedDay1Date: project.estimatedDay1Date,
                    showWonProposal: shouldShowWonProposal(project.projectStageId),
                    addressId: project.ovaid,
                    name: project.name,
                    price: project.price,
                    cppLength: address.cppLength,
                    warrantyLength: project.warrantyLength || 0,
                    isNew: address.primaryProjectId === project.ovprjid,
                    orgNotes: address.orgNotes || '',
                    clientSelectedMembership: address.clientSelectedMembership,
                    clientActivatedMembership: address.clientActivatedMembership,
                    membershipSalesNotes: address.membershipSalesNotes || '',
                    tosAccepted: address.tosAccepted,
                    projectStageId: project.projectStageId,
                });
            };
            const shouldShowProjectDataLine = (stageId) => stageId === DzProjectStage.Lead;
            const getProjectWarrantyData = (warrantyExpirationDate) => {
                if (!warrantyExpirationDate) {
                    return {
                        icon: (_jsx(CalendarTodayIcon, { fontSize: "small", htmlColor: theme.dz.colors.oneVisionBlack65 })),
                        type: 'today',
                    };
                }
                else if (isAfter(new Date(), new Date(warrantyExpirationDate)) &&
                    !isToday(new Date(warrantyExpirationDate))) {
                    return {
                        icon: (_jsx(EventBusy, { style: { fontSize: 20 }, htmlColor: theme.dz.colors.error })),
                        type: 'error',
                    };
                }
                else {
                    return {
                        icon: (_jsx(EventAvailableIcon, { style: { fontSize: 20 }, htmlColor: theme.dz.colors.success })),
                        type: 'success',
                    };
                }
            };
            const shouldDisplayDay1Date = (stageId) => stageId === DzProjectStage.ServiceClient;
            const shouldDisplayEstimatedDay1Date = (stageId) => stageId === DzProjectStage.Lead ||
                stageId === DzProjectStage.ActiveProject;
            const shouldDisplayWarrantyExpiration = (stageId) => stageId === DzProjectStage.ServiceClient;
            const shouldDisableWarrantyField = (value) => !value || isPast(new Date(value));
            const warrantyData = getProjectWarrantyData(project.warrantyExpirationDate);
            const owner = owners.find((owner) => owner.ownerId === project.ownerId);
            const onCardNameClickHandler = () => onCardNameClick(project.ovprjid);
            return (_jsxs("div", Object.assign({ className: classes.cardContainer }, { children: [_jsxs("div", Object.assign({ className: csn(classes.headerContainer, [
                            classes.headerContainerZen,
                            zendesk,
                        ]) }, { children: [_jsx("div", Object.assign({ className: classes.cardHeader }, { children: _jsx(DzCardHeaderName, { copyText: project.name || (address === null || address === void 0 ? void 0 : address.address1), openText: 'Open Project', onClick: onCardNameClickHandler, hideTooltip: zendesk }) })), _jsx(IconButton, Object.assign({ className: classes.projectMenuButton, size: "small", onClick: handleMenuClick }, { children: _jsx(MoreVertIcon, {}) }))] })), _jsx("div", Object.assign({ className: csn(classes.innerContainer, [
                            classes.innerContainerZen,
                            zendesk,
                        ]) }, { children: _jsxs("div", Object.assign({ className: classes.projectDataContainer }, { children: [_jsxs(DzProjectsDataRow, Object.assign({ title: "Stage", shouldDisplay: Boolean(project.projectStage), zendesk: zendesk }, { children: [_jsx("div", Object.assign({ className: classes.projectIcon }, { children: getProjectStageIcon(project.projectStageId) })), _jsx("div", Object.assign({ className: csn(classes.orgDataValue, [
                                                orgClasses.projectStage,
                                                zendesk,
                                            ]) }, { children: getIsFuture() &&
                                                project.projectStageId === DzProjectStage.ServiceClient
                                                ? 'Active Project'
                                                : project.projectStage.replace('Lead', 'Proposal') }))] })), _jsx(DzProjectsDataRow, Object.assign({ title: "Project Manager", shouldDisplay: true, zendesk: zendesk }, { children: address && (_jsx(DzOwnerAutocomplete, { dispatch: dispatch, owner: owner, owners: owners, address: address, projectId: project.ovprjid, thunks: thunks })) })), _jsx(DzProjectsDataRow, Object.assign({ title: "Est. Signing", shouldDisplay: shouldShowProjectDataLine(project.projectStageId), zendesk: zendesk }, { children: _jsx(DzCalendarPicker, Object.assign({ fieldKey: "signingDate", projectId: project.ovprjid, value: project.signingDate, onChange: handleDateChange }, { children: _jsx(CalendarTodayIcon, { fontSize: "small", htmlColor: theme.dz.colors.oneVisionBlack65 }) })) })), _jsx(DzProjectsDataRow, Object.assign({ title: "Day1 Date", shouldDisplay: shouldDisplayDay1Date(project.projectStageId), zendesk: zendesk }, { children: _jsx(DzCalendarPicker, Object.assign({ fieldKey: "day1StartDate", projectId: project.ovprjid, value: project.day1StartDate, onChange: handleDateChange }, { children: _jsx(CalendarTodayIcon, { fontSize: "small", htmlColor: theme.dz.colors.oneVisionBlack65 }) })) })), _jsx(DzProjectsDataRow, Object.assign({ title: "Est. Day1", shouldDisplay: shouldDisplayEstimatedDay1Date(project.projectStageId), zendesk: zendesk }, { children: _jsx(DzCalendarPicker, Object.assign({ fieldKey: "estimatedDay1Date", projectId: project.ovprjid, value: project.estimatedDay1Date, onChange: handleDateChange, zendesk: zendesk }, { children: _jsx(CalendarTodayIcon, { fontSize: "small", htmlColor: theme.dz.colors.oneVisionBlack65 }) })) })), _jsx(DzProjectsDataRow, Object.assign({ title: "Warranty Expiration", shouldDisplay: shouldDisplayWarrantyExpiration(project.projectStageId), zendesk: zendesk }, { children: _jsx(DzCalendarPicker, Object.assign({ disabled: shouldDisableWarrantyField(project.warrantyExpirationDate), fieldKey: "warrantyExpirationDate", projectId: project.ovprjid, value: project.warrantyExpirationDate, onChange: handleDateChange, success: warrantyData.type === 'success', error: warrantyData.type === 'error' }, { children: warrantyData.icon })) })), _jsx(DzProjectsDataRow, Object.assign({ title: "Lost Reason", shouldDisplay: Boolean(project.projectLostReason || project.lostReasonNotes), justifyTitle: "top", zendesk: zendesk }, { children: project.projectLostReason && (_jsx("div", Object.assign({ className: orgClasses.orgDataValue }, { children: `${project.projectLostReason}. ${project.lostReasonNotes || ''}` }))) }))] })) }))] }), project.ovprjid));
        });
    }, [projectsList, owners, address, onCardNameClick]);
    if (!projects) {
        return _jsx(DzEmpty, { title: "No Projects Were Found" });
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: classes.projectsRoot }, { children: [_jsxs("div", Object.assign({ className: csn(classes.tabContentHeader, [
                            classes.tabContentHeaderZen,
                            zendesk,
                        ]) }, { children: [_jsx(Box, Object.assign({ width: "50%" }, { children: _jsx(DzSearchInput, { placeholder: "Filter results", onTextChange: searchHandler, zendesk: zendesk }) })), _jsx(Button, Object.assign({ color: "primary", disabled: clientsLoading, onClick: openNewProjectDialog }, { children: "+ Add Project" }))] })), _jsx("div", { children: projectsContent })] })), _jsx(DzMenuView, { projectMenuInfo: projectMenuInfo, setProjectMenuInfo: setProjectMenuInfo, actions: actions, dispatch: dispatch, address: address, apiClient: apiClient, thunks: thunks, zendesk: zendesk, partner: partner })] }));
};
